import {
  ChartOptions,
  createChart as createLightweightChart,
  DeepPartial,
  PriceFormatCustom,
  PriceFormatBuiltIn,
  SeriesMarker,
  Time,
} from 'lightweight-charts';

import {
  formatPercentage,
  formatDollars,
  DataType,
  BacktestType,
} from '@sc1/common/utils';
import { base } from '../styles/base';

export type TMarker = SeriesMarker<Time>;

export type IStats = Record<string, string | number>;

export function createChart(
  chartId: string,
  chartStyles: DeepPartial<ChartOptions>
) {
  document.getElementById(chartId)!.innerHTML = '';
  return createLightweightChart(chartId, chartStyles);
}

export const createMarker = (
  time: number,
  text = '',
  moreSettings?: Partial<TMarker>
): TMarker => ({
  time: time as Time,
  text: text,
  position: 'aboveBar',
  shape: 'arrowDown',
  color: base.colors.lightBlue,
  size: 1,
  ...moreSettings,
});

export function getPriceFormat(
  dataType
):
  | DeepPartial<PriceFormatBuiltIn>
  | DeepPartial<PriceFormatCustom>
  | undefined {
  switch (dataType) {
    case 'volatility':
      return { type: 'percent' };
    case 'yield':
      return {
        type: 'custom',
        minMove: 0.001,
        formatter: (x) => x.toFixed(2) + '%',
      };
    default:
      return undefined;
  }
}

export function getBacktestOptions({
  asset,
  position,
  rebalanceThreshold,
  days,
  positionRange,
}: {
  asset: string;
  position: BacktestType;
  rebalanceThreshold: number;
  positionRange: number;
  days?: string;
}) {
  if (!days || days === '') days = position === 'perpetual' ? '180' : '365';
  let options: any = {
    asset: position === 'perpetual' ? asset?.toLowerCase() : asset,
    rebalanceThreshold,
    days,
  };
  if (position === 'perpetual') options = { ...options, positionRange };
  return options;
}

export function getMarkers(data: any[]): TMarker[] {
  return data.reduce((acc, x) => {
    if (x.rebalance)
      acc.push(
        createMarker(x.timestamp, undefined, {
          color: base.colors.green,
          position: 'inBar',
          shape: 'circle',
        })
      );
    return acc;
  }, []);
}

export function formatSeriesValue(value, series: BacktestType | DataType) {
  switch (series) {
    case 'yield':
    case 'volatility':
      return value * 100;
    case 'longLp':
    case 'shortLp':
      return (value - 1) * 100;
    default:
      return value;
  }
}

export function formatAssetData(x: any, dataType: DataType) {
  return x.data.map((d) => ({
    value: formatSeriesValue(d.value, dataType),
    time: Math.round(d.timestamp / 1000),
  }));
}
